import React, { useRef, useState } from "react";
import { FiUpload } from "react-icons/fi";
import { uploadFile } from "../services/uploadService";

interface DropZoneUploadProps {
  label?: string;
  value: string;
  onChange?: (url: string) => void;
  className?: string;
  height?: number; // ความสูงของกล่อง preview
  circle?: boolean; // ให้ preview เป็นวงกลมหรือไม่
  readOnly?: boolean; // โหมดอ่านอย่างเดียว
}

const DropZoneUpload: React.FC<DropZoneUploadProps> = ({
  label,
  value,
  onChange,
  className = "",
  height = 140,
  circle = false,
  readOnly = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File | undefined) => {
    if (!file || readOnly) return;

    try {
      setUploading(true);
      const uploaded = await uploadFile(file);
      onChange?.(uploaded.data.file.path);
    } catch (err) {
      console.error("Upload failed:", err);
      // OPTIONAL: แนะนำ trigger alert error จาก parent
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (readOnly) return;
    setIsDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (readOnly) return;
    setIsDragging(true);
  };

  const onDragLeave = () => {
    if (readOnly) return;
    setIsDragging(false);
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {label && (
        <label className="block text-sm">
          {label}{" "}
          {readOnly && (
            <span className="text-[11px] text-gray-400">(read only)</span>
          )}
        </label>
      )}

      <div
        className={`
          border-2 border-dashed rounded-xl px-4 py-5
          flex flex-col items-center justify-center gap-2
          transition-all
          ${
            readOnly
              ? "border-gray-200 bg-gray-50 cursor-default"
              : isDragging
              ? "border-navy bg-navy/5 cursor-pointer"
              : "border-gray-300 bg-gray-50 cursor-pointer"
          }
        `}
        style={{ minHeight: height }}
        onClick={() => {
          if (!readOnly) inputRef.current?.click();
        }}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        {/* When uploaded show preview */}
        {value ? (
          <>
            <div
              className={`overflow-hidden bg-white w-full h-48 flex items-center justify-center`}
              style={{
                borderRadius: circle ? "50%" : "10px",
              }}
            >
              <img
                src={value}
                alt="preview"
                className="w-full h-full object-contain"
              />
            </div>
            {readOnly ? (
              <p className="text-xs text-gray-400">Preview only (read only)</p>
            ) : (
              <p className="text-xs text-gray-500">
                Click or drag & drop to replace image
              </p>
            )}
          </>
        ) : readOnly ? (
          <p className="text-xs text-gray-400">No image</p>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center border">
              {uploading ? (
                <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <FiUpload className="text-gray-500 text-xl" />
              )}
            </div>
            <p className="text-xs text-gray-600 text-center">
              Drag & drop image here, or{" "}
              <span className="font-semibold">click to browse</span>
            </p>
            <p className="text-[10px] text-gray-400">JPG / PNG / SVG</p>
          </>
        )}

        {/* hidden input */}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          disabled={readOnly}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
    </div>
  );
};

export default DropZoneUpload;
